import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Gift, Spinner, CheckCircle, XCircle, Copy, Wallet, Cloud,
} from "@phosphor-icons/react";
import api from "../../lib/api";
import { flag, fmtEUR, fmtData, countryName } from "../../lib/format";

function StatusPill({ status }) {
  const map = {
    fulfilled:     { bg: "bg-mint-soft",  text: "text-mint",        Icon: CheckCircle, label: "Fulfilled" },
    paid:          { bg: "bg-cobalt-50",  text: "text-cobalt-600",  Icon: Spinner,     label: "Queued" },
    provisioning:  { bg: "bg-coral-soft", text: "text-coral-deep",  Icon: Spinner,     label: "Provisioning" },
    provisioning_failed: { bg: "bg-red-50",text: "text-destructive",Icon: XCircle,     label: "Failed" },
  };
  const v = map[status] || { bg: "bg-ink-100", text: "text-ink-700", Icon: Spinner, label: status };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold ${v.bg} ${v.text}`}>
      <v.Icon size={12} weight="bold" className={status === "paid" || status === "provisioning" ? "animate-spin" : ""} /> {v.label}
    </span>
  );
}

export default function AdminGifts() {
  const { t, i18n } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [planQuery, setPlanQuery] = useState("");
  const [planId, setPlanId] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);  // {order, esim}
  const [error, setError] = useState("");
  const [balance, setBalance] = useState(null);
  const [stats, setStats] = useState(null);
  const [bulkInput, setBulkInput] = useState("");
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data));
    api.get("/admin/yesim/balance").then(({ data }) => setBalance(data));
    api.get("/admin/gifts/stats").then(({ data }) => setStats(data));
  }, []);

  const filteredPlans = useMemo(() => {
    const q = planQuery.trim().toLowerCase();
    const list = q
      ? plans.filter((p) =>
          (p.name || "").toLowerCase().includes(q) ||
          (p.country || "").toLowerCase().includes(q) ||
          (p.country_iso2 || "").toLowerCase().includes(q),
        )
      : plans;
    return list.slice(0, 60);
  }, [plans, planQuery]);

  const selectedPlan = plans.find((p) => p.id === planId);

  const giftOne = async () => {
    setError(""); setResult(null); setSubmitting(true);
    try {
      const { data } = await api.post("/admin/orders/gift", { plan_id: planId, email, name, note, send_email: sendEmail });
      // Poll for fulfillment
      const orderId = data.order_id;
      let attempts = 0;
      while (attempts < 15) {
        await new Promise((r) => setTimeout(r, 1500));
        const { data: status } = await api.get(`/admin/orders/${orderId}/status`);
        if (status.order.status === "fulfilled" || status.order.status === "provisioning_failed") {
          setResult(status);
          break;
        }
        attempts++;
      }
      if (!result) {
        const { data: status } = await api.get(`/admin/orders/${orderId}/status`);
        setResult(status);
      }
      // Refresh stats
      api.get("/admin/gifts/stats").then(({ data }) => setStats(data));
    } catch (e) {
      setError(e.response?.data?.detail || "Gift failed");
    } finally {
      setSubmitting(false);
    }
  };

  const giftBulk = async () => {
    setBulkResult(null); setBulkSubmitting(true);
    try {
      // Parse: "email,plan_id" per line OR "email,country_iso2,data_gb,days" → pick first matching plan
      const lines = bulkInput.split("\n").map((l) => l.trim()).filter(Boolean);
      const items = [];
      for (const line of lines) {
        const parts = line.split(",").map((s) => s.trim());
        if (parts.length >= 2) {
          const [em, pid] = parts;
          // If pid looks like an ISO country code (2 letters), try to resolve to a plan
          if (pid.length === 2 && /^[a-z]{2}$/i.test(pid)) {
            const match = plans.find((p) => p.country_iso2 === pid.toUpperCase());
            if (match) items.push({ email: em, plan_id: match.id });
          } else {
            items.push({ email: em, plan_id: pid });
          }
        }
      }
      if (items.length === 0) {
        setBulkResult({ error: "No valid rows. Format: email,plan_id (one per line)" });
        return;
      }
      const { data } = await api.post("/admin/orders/gift-bulk", { items, send_email: true });
      setBulkResult(data);
      api.get("/admin/gifts/stats").then(({ data }) => setStats(data));
    } catch (e) {
      setBulkResult({ error: e.response?.data?.detail || "Bulk failed" });
    } finally {
      setBulkSubmitting(false);
    }
  };

  const copy = (txt) => navigator.clipboard?.writeText(txt);

  return (
    <div data-testid="admin-gifts">
      <div className="overline mb-2">{t("admin.gifts", "Manual provisioning")}</div>
      <div className="flex items-baseline justify-between flex-wrap gap-4 mb-8">
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tightest">
          {t("admin.giftTitle", "Gift an eSIM")}
        </h1>
        <span className="text-sm text-ink-500">
          {t("admin.giftSub", "Provision a real eSIM via Yesim with no payment. Wholesale cost is billed to your prepaid Yesim balance.")}
        </span>
      </div>

      {/* Yesim balance + month-to-date gift spend */}
      <div className="grid sm:grid-cols-3 gap-5 mb-8">
        <div className="card-soft p-5 flex items-center gap-4" data-testid="yesim-balance-card">
          <div className="w-12 h-12 rounded-2xl bg-cobalt-50 flex items-center justify-center text-cobalt-500">
            <Wallet size={22} weight="bold" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="overline mb-1">{t("admin.yesimBalance", "Yesim balance")}</div>
            <div className="font-display font-black text-xl tracking-tight">
              {balance?.balance_eur != null ? fmtEUR(balance.balance_eur) : "—"}
            </div>
            {balance?.note && <div className="text-[11px] text-ink-500 mt-1 line-clamp-2">{balance.note}</div>}
          </div>
        </div>
        <div className="card-soft p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-coral-soft flex items-center justify-center text-coral-deep">
            <Gift size={22} weight="bold" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="overline mb-1">{t("admin.giftsThisMonth", "Gifts · last 30 days")}</div>
            <div className="font-display font-black text-xl tracking-tight">{stats?.count ?? "—"}</div>
            <div className="text-[11px] text-ink-500 mt-1">{stats?.total_wholesale_cost_eur != null ? `Cost: ${fmtEUR(stats.total_wholesale_cost_eur)}` : ""}</div>
          </div>
        </div>
        <div className="card-soft p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-mint-soft flex items-center justify-center text-mint">
            <Cloud size={22} weight="bold" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="overline mb-1">{t("admin.yesimMode", "Yesim mode")}</div>
            <div className="font-display font-black text-xl tracking-tight">{balance?.mode === "dev" ? "MOCK" : "LIVE"}</div>
            <div className="text-[11px] text-ink-500 mt-1">{balance?.mode === "dev" ? "Set YESIM_API_TOKEN to flip" : "Real provisioning"}</div>
          </div>
        </div>
      </div>

      {/* Single gift card */}
      <div className="card-soft p-6 lg:p-8 mb-8">
        <h2 className="font-display font-bold text-xl tracking-tight mb-5">{t("admin.giftOne", "Gift one eSIM")}</h2>
        <div className="grid lg:grid-cols-2 gap-5 mb-5">
          <div>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-widest text-ink-500">{t("common.plan", "Plan")}</span>
              <input
                type="text" value={planQuery} onChange={(e) => setPlanQuery(e.target.value)}
                placeholder={t("admin.planSearch", "Search by country, ISO2, or plan name…")}
                className="input-soft mt-1"
                data-testid="gift-plan-search"
              />
            </label>
            <div className="mt-2 max-h-56 overflow-y-auto rounded-2xl border border-ink-100 bg-white">
              {filteredPlans.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPlanId(p.id)}
                  className={`w-full text-left px-3 py-2 hover:bg-cobalt-50/60 flex items-center justify-between gap-3 ${planId === p.id ? "bg-cobalt-50 ring-1 ring-cobalt-300" : ""}`}
                  data-testid={`gift-plan-${p.id}`}
                >
                  <span className="text-sm flex items-center gap-2 min-w-0">
                    <span className="text-base flex-shrink-0">{flag(p.country_iso2)}</span>
                    <span className="truncate">{p.name} · {countryName(p.country_iso2, i18n.language) || p.country}</span>
                  </span>
                  <span className="text-xs font-mono text-ink-500 flex-shrink-0">{fmtData(p.data_mb)} · {p.days}d · cost {fmtEUR(p.wholesale_price)}</span>
                </button>
              ))}
              {filteredPlans.length === 0 && <div className="p-4 text-sm text-ink-500 text-center">No matches</div>}
            </div>
          </div>
          <div className="space-y-3">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-widest text-ink-500">{t("common.email", "Email")}</span>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="customer@example.com" className="input-soft mt-1"
                data-testid="gift-email"
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-widest text-ink-500">{t("common.nameOptional", "Name (optional)")}</span>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="input-soft mt-1" data-testid="gift-name" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-widest text-ink-500">{t("admin.giftNote", "Internal note (optional)")}</span>
              <input
                type="text" value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Influencer · TikTok campaign · CS make-good" className="input-soft mt-1"
                data-testid="gift-note"
              />
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="w-4 h-4 accent-cobalt-500" data-testid="gift-send-email" />
              <span>{t("admin.giftEmail", "Email QR code to customer (recommended)")}</span>
            </label>
          </div>
        </div>
        {selectedPlan && (
          <div className="rounded-2xl bg-ink-50 border border-ink-100 p-4 mb-5 text-sm">
            <span className="font-semibold">{t("admin.giftPreview", "You will provision")}: </span>
            {flag(selectedPlan.country_iso2)} <b>{selectedPlan.name}</b> ({countryName(selectedPlan.country_iso2, i18n.language) || selectedPlan.country}) — {fmtData(selectedPlan.data_mb)} · {selectedPlan.days}d ·
            <span className="text-coral-deep font-semibold ml-1">cost {fmtEUR(selectedPlan.wholesale_price)} to your Yesim balance</span>
          </div>
        )}
        <button
          type="button"
          onClick={giftOne}
          disabled={!planId || !email || submitting}
          className="btn-primary disabled:opacity-50"
          data-testid="gift-submit"
        >
          {submitting ? <><Spinner size={16} className="animate-spin" /> {t("admin.provisioning", "Provisioning…")}</> :
            <><Gift size={16} weight="bold" /> {t("admin.gift", "Gift eSIM")}</>}
        </button>
        {error && (
          <div className="mt-4 p-4 rounded-2xl bg-red-50 text-destructive text-sm" data-testid="gift-error">{error}</div>
        )}
        {result && (
          <div className="mt-6 rounded-3xl bg-mint-soft/40 border border-mint/30 p-5" data-testid="gift-result">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <StatusPill status={result.order.status} />
                <span className="text-sm font-semibold">{t("admin.orderId", "Order")} #{result.order.id.slice(0, 8)}</span>
              </div>
              <button onClick={() => copy(result.order.id)} className="text-xs text-cobalt-600 hover:underline flex items-center gap-1">
                <Copy size={12} /> {t("admin.copyId", "Copy id")}
              </button>
            </div>
            {result.esim ? (
              <div className="grid sm:grid-cols-[auto_1fr] gap-5 items-start">
                {result.esim.qr_image_base64 && (
                  <img
                    src={result.esim.qr_image_base64.startsWith("data:") ? result.esim.qr_image_base64 : `data:image/png;base64,${result.esim.qr_image_base64}`}
                    alt="eSIM QR"
                    width={180} height={180}
                    className="rounded-2xl bg-white border border-ink-100 p-2"
                    data-testid="gift-qr"
                  />
                )}
                <div className="text-sm space-y-1.5 min-w-0">
                  <div><b>ICCID:</b> <code className="text-xs">{result.esim.iccid}</code></div>
                  <div><b>IMSI:</b> <code className="text-xs">{result.esim.imsi}</code></div>
                  <div><b>MSISDN:</b> <code className="text-xs">{result.esim.msisdn}</code></div>
                  <div className="break-all"><b>QR text:</b> <code className="text-xs">{result.esim.qr_code_text}</code></div>
                  {result.esim.ios_install_link && (
                    <div><a href={result.esim.ios_install_link} target="_blank" rel="noreferrer" className="text-cobalt-600 hover:underline text-xs">Apple Universal Install Link →</a></div>
                  )}
                  {result.esim.passport_link && (
                    <div><a href={result.esim.passport_link} target="_blank" rel="noreferrer" className="text-cobalt-600 hover:underline text-xs">Yesim Passport →</a></div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-sm text-ink-500">{t("admin.provisioning", "Provisioning…")} ({result.order.provisioning_attempts} attempts)</div>
            )}
            {result.order.provisioning_error && (
              <div className="mt-3 text-xs text-destructive bg-red-50 rounded-xl p-2"><b>Error:</b> {result.order.provisioning_error}</div>
            )}
          </div>
        )}
      </div>

      {/* Bulk gift card */}
      <div className="card-soft p-6 lg:p-8">
        <h2 className="font-display font-bold text-xl tracking-tight mb-2">{t("admin.giftBulk", "Bulk gift")}</h2>
        <p className="text-sm text-ink-500 mb-4">
          {t("admin.giftBulkHelp", "Paste one row per line: `email,plan_id` OR `email,COUNTRY_ISO2` (picks the cheapest plan for that country). Max 200 rows.")}
        </p>
        <textarea
          value={bulkInput}
          onChange={(e) => setBulkInput(e.target.value)}
          placeholder={"alice@example.com,GR\nbob@example.com,7b4adf45-6690-415b-bf6d-52ddda6d721a\ncharlie@example.com,TR"}
          rows={6}
          className="input-soft font-mono text-xs"
          data-testid="bulk-textarea"
        />
        <button
          type="button"
          onClick={giftBulk}
          disabled={!bulkInput.trim() || bulkSubmitting}
          className="btn-outline mt-3 disabled:opacity-50"
          data-testid="bulk-submit"
        >
          {bulkSubmitting ? "…" : t("admin.giftBulkSubmit", "Queue bulk gift")}
        </button>
        {bulkResult && (
          <div className="mt-4 rounded-2xl bg-cobalt-50 p-4 text-sm" data-testid="bulk-result">
            {bulkResult.error ? (
              <div className="text-destructive">{bulkResult.error}</div>
            ) : (
              <>
                <div className="font-semibold">{t("admin.bulkQueued", "Queued")}: {bulkResult.queued} · {t("admin.bulkSkipped", "Skipped")}: {bulkResult.skipped} · {t("admin.totalCost", "Total cost")}: {fmtEUR(bulkResult.total_wholesale_cost_eur || 0)}</div>
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer">Details</summary>
                  <pre className="mt-1 max-h-64 overflow-auto bg-white rounded-xl p-2">{JSON.stringify(bulkResult.items, null, 2)}</pre>
                </details>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
